import type { RefObject } from "react";

import { useRef, useState } from "react";

import { useObservedRef } from "../../util/useObservedRef";

export type OverflowState = {
  bottom: boolean;
  left: boolean;
  right: boolean;
  top: boolean;
};

export const useHasOverflow = <Element extends HTMLElement>(): [
  RefObject<Element>,
  OverflowState
] => {
  const valueRef = useRef<OverflowState>({
    bottom: false,
    left: false,
    right: false,
    top: false,
  });
  const [, refresh] = useState({});

  const elementRef = useObservedRef<Element>(() => {
    if (!elementRef.current) {
      return;
    }

    const nextValue: OverflowState = {
      bottom:
        elementRef.current.scrollHeight >
        elementRef.current.clientHeight + elementRef.current.scrollTop,
      left: elementRef.current.scrollLeft > 0,
      right:
        elementRef.current.scrollWidth >
        elementRef.current.clientWidth + elementRef.current.scrollLeft,
      top: elementRef.current.scrollTop > 0,
    };

    const hasChange = Object.keys(nextValue).some(
      (key) =>
        valueRef.current[key as keyof OverflowState] !==
        nextValue[key as keyof OverflowState]
    );

    if (hasChange) {
      valueRef.current = nextValue;
      refresh({});
    }
  }, []);

  return [elementRef, valueRef.current];
};
